import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Wrench, CalendarClock, AlertTriangle, ClipboardList, ShieldAlert, Plus, Pencil, Trash2, CheckCircle2, Cpu,
} from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { hasQuotePermission } from '@/lib/quotePermissions';
import {
  RecordDialog, RecordDetailDialog, DeviceDialog, PlanDialog, CustomerSelect,
} from '@/components/quotes/ServiceDialogs';
import { SerialSearch } from '@/components/quotes/SerialSearch';
import { showApiError } from '@/lib/apiErrors';

const API_URL = process.env.REACT_APP_API_URL;
const LOCALES = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' };

const DUE_CLASS = {
  overdue: 'bg-red-50 text-red-700',
  soon: 'bg-amber-50 text-amber-800',
  later: 'bg-slate-100 text-slate-600',
  none: 'bg-slate-100 text-slate-600',
};
const WARRANTY_CLASS = {
  expired: 'text-red-600',
  ending: 'text-amber-700',
  active: 'text-emerald-700',
  none: 'text-[#94A3B8]',
};

export function QuoteService() {
  const { t, i18n } = useTranslation('quotes');
  const lang = (i18n.language || 'tr').split('-')[0];
  const locale = LOCALES[lang] || 'tr-TR';
  const { user } = useAuth();
  const canUseStock = hasQuotePermission(user, 'stock');
  const [params, setParams] = useSearchParams();

  const fmtQty = (n) => new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(Number(n) || 0);
  const fmtDate = (iso) => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale);
  };

  const [tab, setTab] = useState(params.get('customer') ? 'customers' : 'plans');
  const [overview, setOverview] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [records, setRecords] = useState([]);
  const [recordFilter, setRecordFilter] = useState('all');
  const [customerId, setCustomerId] = useState(params.get('customer') || '');
  const [devices, setDevices] = useState([]);
  const [customerRecords, setCustomerRecords] = useState([]);
  const [customerPlan, setCustomerPlan] = useState(null);
  const [warrantyFilter, setWarrantyFilter] = useState(null);
  const [warrantyDevices, setWarrantyDevices] = useState([]);

  const [recordDialog, setRecordDialog] = useState(null); // preset object or null
  const [detail, setDetail] = useState(null);
  const [deviceDialog, setDeviceDialog] = useState(null); // {device} or {}
  const [planOpen, setPlanOpen] = useState(false);

  const loadOverview = () => axios.get(`${API_URL}/api/quote/service/overview`)
    .then(({ data }) => setOverview(data)).catch(() => {});
  const loadPlans = () => axios.get(`${API_URL}/api/quote/service/plans`)
    .then(({ data }) => setPlans(data.items || [])).catch((err) => showApiError(err, t));
  const loadRecords = () => axios.get(`${API_URL}/api/quote/service/records`, {
    params: { status: recordFilter === 'all' ? undefined : recordFilter, page_size: 200 },
  }).then(({ data }) => setRecords(data.items || [])).catch((err) => showApiError(err, t));

  const loadCustomer = async (id = customerId) => {
    if (!id) return;
    try {
      const [d, r, p] = await Promise.all([
        axios.get(`${API_URL}/api/quote/service/devices`, { params: { customer_id: id } }),
        axios.get(`${API_URL}/api/quote/service/records`, { params: { customer_id: id, page_size: 200 } }),
        axios.get(`${API_URL}/api/quote/service/plans`, { params: { customer_id: id } }),
      ]);
      setDevices(d.data.items || []);
      setCustomerRecords(r.data.items || []);
      setCustomerPlan((p.data.items || [])[0] || null);
    } catch (err) {
      showApiError(err, t);
    }
  };

  const loadCustomers = () => axios.get(`${API_URL}/api/quote/customers`, { params: { page_size: 200, is_active: true } })
    .then(({ data }) => setCustomers(data.items || []))
    .catch(() => setCustomers([]));

  const refreshAll = (_record, customer) => {
    if (customer?.created) loadCustomers();
    loadOverview();
    loadPlans();
    loadRecords();
    if (customerId) loadCustomer();
    if (warrantyFilter) loadWarranty(warrantyFilter);
  };

  const loadWarranty = (kind) => axios.get(`${API_URL}/api/quote/service/devices`, { params: { warranty: kind } })
    .then(({ data }) => setWarrantyDevices(data.items || [])).catch(() => setWarrantyDevices([]));

  useEffect(() => {
    loadOverview();
    loadPlans();
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordFilter]);

  useEffect(() => {
    if (customerId) {
      loadCustomer(customerId);
      if (params.get('customer') !== customerId) setParams({ customer: customerId }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const openCustomer = (id) => {
    setCustomerId(id);
    setTab('customers');
  };

  const removeDevice = async (d) => {
    if (!window.confirm(t('service.device.deleteConfirm', { name: d.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/service/devices/${d.id}`);
      loadCustomer();
      loadOverview();
    } catch (err) {
      showApiError(err, t);
    }
  };

  const showWarranty = (kind) => {
    setWarrantyFilter(kind);
    loadWarranty(kind);
    setTab('warranty');
  };

  const customerName = useMemo(() => (customers.find((c) => c.id === customerId) || {}).name || '', [customers, customerId]);

  const RecordRow = ({ r, showCustomer }) => (
    <li>
      <button type="button" onClick={() => setDetail(r)} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50"
        data-testid={`record-row-${r.id}`}>
        <div className="w-20 shrink-0 text-xs text-[#595959]">{fmtDate(r.performed_at)}</div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {showCustomer && <span className="font-medium text-[#1A1A2E]">{r.customer_name}</span>}
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-[#1E4ED8]">{t(`service.types.${r.type}`)}</span>
            {r.status === 'open' && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">{t('service.status.open')}</span>}
          </div>
          {(r.description || r.result) && (
            <div className="mt-0.5 line-clamp-2 text-sm text-[#475569]">{[r.description, r.result].filter(Boolean).join(' → ')}</div>
          )}
          {r.materials?.length > 0 && (
            <div className="mt-0.5 truncate text-xs text-[#94A3B8]">{r.materials.map((m) => `${m.name} ${fmtQty(m.qty)} ${m.unit}`).join(', ')}</div>
          )}
        </div>
      </button>
    </li>
  );

  const Card = ({ icon: Icon, label, value, tone, onClick, testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
      className="flex items-center gap-3 rounded-2xl border border-[#E5E5E5] bg-white p-3 text-left hover:border-[#2563EB] sm:p-4">
      <Icon className={`hidden h-7 w-7 shrink-0 sm:block ${value ? tone : 'text-[#94A3B8]'}`} />
      <div className="min-w-0">
        <div className="text-[11px] leading-tight text-[#595959] sm:text-xs">{label}</div>
        <div className={`text-lg font-bold sm:text-xl ${value ? tone : 'text-[#1A1A2E]'}`}>{value ?? 0}</div>
      </div>
    </button>
  );

  return (
    <QuoteLayout>
      <div className="mx-auto max-w-6xl" data-testid="quote-service-page">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('service.title')}</h1>
            <p className="mt-1 text-sm text-[#595959]">{t('service.subtitle')}</p>
          </div>
          <Button onClick={() => setRecordDialog({ customer_id: tab === 'customers' ? customerId : '' })}
            className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] sm:w-auto" data-testid="service-new-record">
            <Plus className="mr-2 h-4 w-4" /> {t('service.newRecord')}
          </Button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          <Card icon={AlertTriangle} label={t('service.summary.overdue')} value={overview?.overdue} tone="text-red-600" onClick={() => setTab('plans')} testId="service-card-overdue" />
          <Card icon={CalendarClock} label={t('service.summary.soon')} value={overview?.due_soon} tone="text-amber-700" onClick={() => setTab('plans')} />
          <Card icon={ClipboardList} label={t('service.summary.open')} value={overview?.open_records} tone="text-[#2563EB]" onClick={() => { setRecordFilter('open'); setTab('records'); }} />
          <Card icon={ShieldAlert} label={t('service.summary.warranty')} value={overview?.warranty_ending} tone="text-amber-700" onClick={() => showWarranty('ending')} />
        </div>

        <SerialSearch fmtDate={fmtDate} onOpenCustomer={openCustomer} />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start sm:w-auto">
            <TabsTrigger value="plans" data-testid="service-tab-plans">{t('service.tabs.plans')}</TabsTrigger>
            <TabsTrigger value="records" data-testid="service-tab-records">{t('service.tabs.records')}</TabsTrigger>
            <TabsTrigger value="customers" data-testid="service-tab-customers">{t('service.tabs.customers')}</TabsTrigger>
            {warrantyFilter && <TabsTrigger value="warranty">{t('service.tabs.warranty')}</TabsTrigger>}
          </TabsList>

          {/* ------------------------------------------------ maintenance calendar */}
          <TabsContent value="plans">
            <div className="overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
              {plans.length === 0 ? (
                <div className="p-10 text-center">
                  <CalendarClock className="mx-auto mb-3 h-10 w-10 text-[#94A3B8]" />
                  <p className="text-[#475569]">{t('service.plan.empty')}</p>
                </div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {plans.map((p) => (
                    <li key={p.customer_id} className="flex items-center gap-3 px-4 py-3" data-testid={`plan-row-${p.customer_id}`}>
                      <button type="button" onClick={() => openCustomer(p.customer_id)} className="min-w-0 flex-1 text-left">
                        <div className="line-clamp-2 font-medium text-[#1A1A2E]">{p.customer_name}</div>
                        <div className="text-xs text-[#94A3B8]">
                          {t('service.plan.everyMonths', { count: p.interval_months })}
                          {p.last_done_at ? ` · ${t('service.plan.lastDone', { date: fmtDate(p.last_done_at) })}` : ''}
                        </div>
                      </button>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${DUE_CLASS[p.due_state]}`}>
                        {fmtDate(p.next_due_at)}
                      </span>
                      <Button size="sm" variant="outline" className="shrink-0"
                        onClick={() => setRecordDialog({ customer_id: p.customer_id, type: 'maintenance' })}
                        data-testid={`plan-done-${p.customer_id}`}>
                        <CheckCircle2 className="mr-1.5 h-4 w-4 text-emerald-600" />
                        <span className="hidden sm:inline">{t('service.plan.markDone')}</span>
                        <span className="sm:hidden">{t('service.plan.markDoneShort')}</span>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="mt-2 text-xs text-[#94A3B8]">{t('service.plan.footer')}</p>
          </TabsContent>

          {/* ------------------------------------------------ records */}
          <TabsContent value="records">
            <div className="mb-3 flex gap-2">
              {['all', 'open', 'done'].map((k) => (
                <Button key={k} size="sm" variant={recordFilter === k ? 'default' : 'outline'} onClick={() => setRecordFilter(k)}>
                  {t(`service.filter.${k}`)}
                </Button>
              ))}
            </div>
            <div className="overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
              {records.length === 0 ? (
                <div className="p-10 text-center text-[#94A3B8]">{t('service.record.empty')}</div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {records.map((r) => <RecordRow key={r.id} r={r} showCustomer />)}
                </ul>
              )}
            </div>
          </TabsContent>

          {/* ------------------------------------------------ customer devices */}
          <TabsContent value="customers">
            <div className="mb-4 max-w-md">
              <CustomerSelect customers={customers} value={customerId} onChange={setCustomerId} testId="service-customer-select" />
            </div>
            {!customerId ? (
              <div className="rounded-2xl border border-[#E5E5E5] bg-white p-10 text-center text-[#475569]">
                <Cpu className="mx-auto mb-3 h-10 w-10 text-[#94A3B8]" />
                {t('service.customerHelp')}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#E5E5E5] bg-white p-4">
                  <CalendarClock className="h-5 w-5 text-[#2563EB]" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold">{t('service.plan.title')}</div>
                    <div className="text-sm text-[#595959]">
                      {customerPlan
                        ? t('service.plan.summary', { count: customerPlan.interval_months, date: fmtDate(customerPlan.next_due_at) })
                        : t('service.plan.none')}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setPlanOpen(true)} data-testid="service-plan-edit">
                    {customerPlan ? t('common.edit') : t('service.plan.create')}
                  </Button>
                </div>

                <div className="overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
                  <div className="flex items-center justify-between border-b border-[#F1F5F9] px-4 py-3">
                    <h2 className="font-semibold">{t('service.device.listTitle', { name: customerName })}</h2>
                    <Button size="sm" onClick={() => setDeviceDialog({})} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="service-add-device">
                      <Plus className="mr-1.5 h-4 w-4" /> {t('service.device.add')}
                    </Button>
                  </div>
                  {devices.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#94A3B8]">{t('service.device.empty')}</div>
                  ) : (
                    <ul className="divide-y divide-[#F1F5F9]">
                      {devices.map((d) => (
                        <li key={d.id} className="flex items-start gap-3 px-4 py-3" data-testid={`device-row-${d.id}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-[#1A1A2E]">
                              {d.quantity !== 1 ? `${fmtQty(d.quantity)} × ` : ''}{d.name}
                            </div>
                            <div className="truncate text-xs text-[#94A3B8]">
                              {[d.brand, d.model, d.location].filter(Boolean).join(' · ')}
                            </div>
                            <div className="mt-0.5 text-xs">
                              <span className="text-[#595959]">{t('service.device.installedShort', { date: fmtDate(d.installed_at) })}</span>
                              {d.warranty_until && (
                                <span className={`ml-2 ${WARRANTY_CLASS[d.warranty_state]}`}>
                                  {t(`service.device.warranty.${d.warranty_state}`, { date: fmtDate(d.warranty_until) })}
                                </span>
                              )}
                            </div>
                            {d.serials && <div className="mt-0.5 truncate text-xs text-[#94A3B8]">S/N: {d.serials}</div>}
                          </div>
                          <Button size="icon" variant="ghost" onClick={() => setDeviceDialog({ device: d })}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => removeDevice(d)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
                  <div className="flex items-center gap-2 border-b border-[#F1F5F9] px-4 py-3">
                    <Wrench className="h-4 w-4 text-[#2563EB]" />
                    <h2 className="font-semibold">{t('service.record.historyTitle')}</h2>
                  </div>
                  {customerRecords.length === 0 ? (
                    <div className="p-8 text-center text-sm text-[#94A3B8]">{t('service.record.empty')}</div>
                  ) : (
                    <ul className="divide-y divide-[#F1F5F9]">
                      {customerRecords.map((r) => <RecordRow key={r.id} r={r} />)}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ------------------------------------------------ warranty list */}
          <TabsContent value="warranty">
            <div className="overflow-hidden rounded-2xl border border-[#E5E5E5] bg-white">
              {warrantyDevices.length === 0 ? (
                <div className="p-10 text-center text-[#94A3B8]">{t('service.device.noWarranty')}</div>
              ) : (
                <ul className="divide-y divide-[#F1F5F9]">
                  {warrantyDevices.map((d) => (
                    <li key={d.id}>
                      <button type="button" onClick={() => openCustomer(d.customer_id)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-[#1A1A2E]">{d.customer_name}</div>
                          <div className="truncate text-xs text-[#94A3B8]">{d.quantity !== 1 ? `${d.quantity} × ` : ''}{d.name}</div>
                        </div>
                        <span className={`shrink-0 text-xs ${WARRANTY_CLASS[d.warranty_state]}`}>{fmtDate(d.warranty_until)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <RecordDialog open={!!recordDialog} preset={recordDialog} onClose={() => setRecordDialog(null)}
        onSaved={refreshAll} customers={customers} canUseStock={canUseStock} />
      <RecordDetailDialog record={detail} onClose={() => setDetail(null)} onSaved={refreshAll} />
      <DeviceDialog open={!!deviceDialog} device={deviceDialog?.device} customerId={customerId}
        onClose={() => setDeviceDialog(null)} onSaved={() => { loadCustomer(); loadOverview(); }} />
      <PlanDialog open={planOpen} plan={customerPlan} customerId={customerId}
        onClose={() => setPlanOpen(false)} onSaved={() => { loadCustomer(); loadPlans(); loadOverview(); }} />
    </QuoteLayout>
  );
}
